import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["content", "turboFrame"];
  static values = {
    autoOpen: Boolean,
    openOnLoadWithParams: Boolean,
    openOnLoadWithLocalStorage: String,
  };

  async connect() {
    window.HSStaticMethods.autoInit();
    this.addEventListeners();

    if (this.autoOpenValue) {
      await this.openModal(this.element.id);
      return;
    }

    if (this.openOnLoadWithParamsValue) {
      const urlParams = new URLSearchParams(window.location.search);
      const openModalParam = urlParams.get(this.element.id);

      if (openModalParam === "true") {
        await this.openModal(this.element.id);
      }
      return;
    }

    if (this.openOnLoadWithLocalStorageValue) {
      const localStorageValue = localStorage.getItem(this.openOnLoadWithLocalStorageValue);

      if (localStorageValue === "true") return;

      await this.openModal(this.element.id);
      localStorage.setItem(this.openOnLoadWithLocalStorageValue, "true")
    }
  }

  addEventListeners() {
    document.addEventListener("open.hs.overlay", (event) => {
      if (event.target.dataset.hsOverlay === `#${this.element.id}`) {
        this.loadTurboFrames();
        this.loadIframes();
        this.adjustModalPadding();
      }
    });
    document.addEventListener("close.hs.overlay", (event) => {
      if (event.target.dataset.hsOverlay === `#${this.element.id}`) {
        this.unloadIframes();
        this.pauseVideos();
      }
    });
  }

  async openModal(selector) {
    await this.waitForHSOverlay();
    const modalInstance = window.HSOverlay.getInstance(`#${selector}`);
    if (modalInstance) {
      window.HSOverlay.open(`#${selector}`);
      this.clearUrlParam(selector);
      this.loadTurboFrames();
      this.loadIframes();
      this.adjustModalPadding();
    }
  }

  loadTurboFrames() {
    this.turboFrameTargets.forEach((turboFrame) => {
      if (turboFrame.dataset.src) {
        turboFrame.src = turboFrame.dataset.src;
      }
    });
  }

  loadIframes() {
    this.element.querySelectorAll("iframe[data-src]").forEach((iframe) => {
      iframe.src = iframe.dataset.src;
    });
  }

  unloadIframes() {
    this.element.querySelectorAll("iframe[data-src]").forEach((iframe) => {
      iframe.src = "";
    });
  }

  pauseVideos() {
    this.element.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  }

  unloadTurboFrames() {
    this.turboFrameTargets.forEach((turboFrame) => {
      turboFrame.removeAttribute("src");
    });
  }

  clearUrlParam(param) {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState({}, document.title, url);
  }

  waitForHSOverlay() {
    const maxAttempts = 20;

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const interval = setInterval(() => {
        if (window.HSOverlay && window.HSOverlay.getInstance) {
          clearInterval(interval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          reject("HSOverlay failed to load.");
        }
        attempts++;
      }, 2000);
    });
  }

  adjustModalPadding() {
    setTimeout(() => {
      const needsPadding =
        this.contentTarget.scrollHeight > this.contentTarget.clientHeight &&
        window.innerWidth <= 768;
      this.contentTarget.classList.toggle("pb-12", needsPadding);
    }, 100);
  }

  closeOnSuccess(event) {
    if (event.detail.success) {
      window.HSOverlay.close(`#${this.element.id}`);
    }
  }
}
